/**
 * Review Card Component
 * Displays individual review with all details
 */

import styled from '@emotion/styled';
import { ReviewEntity } from '@/domain/entities/review.entity';
import { useTranslation } from '@/lib/i18n/client';
import { useIntl } from '@/lib/intl';
import { StarRating } from './StarRating';

const Card = styled.div`
  padding: ${({ theme }) => theme.space.lg} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.color};
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${({ theme }) => theme.space.sm};
`;

const CustomerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.xs};
`;

const CustomerName = styled.span`
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ReviewDate = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ReviewContent = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.lg};
  
  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    flex-direction: column;
  }
`;

const ContentText = styled.div`
  flex: 1;
`;

const ReviewTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  margin: ${({ theme }) => theme.space.sm} 0;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ReviewBody = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 ${({ theme }) => theme.space.md};
`;

const CustomerImage = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${({ theme }) => theme.space.xs};
`;

const ReviewImage = styled.img`
  width: 100px;
  height: 100px;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.radius.regular};
`;

const ProductVariant = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const HelpfulSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.md};
  margin-top: ${({ theme }) => theme.space.sm};
`;

const HelpfulButton = styled.button`
  background-color: ${({ theme }) => theme.colors.text.primary};
  color: ${({ theme }) => theme.colors.background.primary};
  border: none;
  border-radius: ${({ theme }) => theme.radius.regular};
  padding: ${({ theme }) => `${theme.space.sm} ${theme.space.md}`};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.9;
  }
`;

const HelpfulCount = styled.span`
  font-size: 13px;
  color: #E07B67;
`;

const VerifiedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.semantic?.positive?.color || '#2f806a'};
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

interface ReviewCardProps {
  review: ReviewEntity;
  onVote?: (reviewId: string, voteType: 'helpful' | 'not_helpful') => void;
}

function formatDate(date: Date, locale: string = 'en-US'): string {
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

// Generate a random customer name from the customer ID for demo purposes
function generateCustomerName(customerId: string): string {
  const firstNames = ['Katie', 'Tracy', 'Debbie', 'Lisa', 'Janet', 'Sarah', 'Emma', 'Maria', 'Jennifer', 'Michelle'];
  const lastInitials = ['S', 'D', 'F', 'P', 'S', 'M', 'T', 'R', 'W', 'B'];
  
  // Use customer ID to deterministically pick a name
  const hash = customerId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const firstName = firstNames[hash % firstNames.length];
  const lastInitial = lastInitials[(hash + 1) % lastInitials.length];
  
  return `${firstName} ${lastInitial}.`;
}

// Generate a sample product variant
function generateProductVariant(reviewId: string): string {
  const colors = ['Black', 'Nude', 'Rose Tan', 'Chocolate', 'White'];
  const sizes = ['S', 'M', 'L', 'XL', '2XL', '3XL'];
  
  const hash = reviewId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const color = colors[hash % colors.length];
  const size = sizes[(hash + 2) % sizes.length];
  
  return `${color} / ${size}`;
}

export function ReviewCard({ review, onVote }: ReviewCardProps) {
  const { t } = useTranslation('pdp');
  const { countryConfig } = useIntl();
  const customerName = review.customerName || generateCustomerName(review.customerId);
  const productVariant = review.productVariant || generateProductVariant(review.id);
  
  const handleHelpfulClick = () => {
    onVote?.(review.id, 'helpful');
  };

  return (
    <Card>
      <CardHeader>
        <CustomerInfo>
          <CustomerName>{customerName}</CustomerName>
          <StarRating rating={review.rating} size="sm" />
        </CustomerInfo>
        <ReviewDate>{formatDate(review.createdAt, countryConfig.locale)}</ReviewDate>
      </CardHeader>

      <ReviewContent>
        <ContentText>
          <ReviewTitle>{review.title}</ReviewTitle>
          <ReviewBody>{review.content}</ReviewBody>
          
          {review.verified && (
            <VerifiedBadge>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
              </svg>
              {t('reviews.verifiedPurchase')}
            </VerifiedBadge>
          )}
          
          <HelpfulSection>
            <HelpfulButton onClick={handleHelpfulClick}>
              {t('reviews.helpful')}
            </HelpfulButton>
            {review.helpful > 0 && (
              <HelpfulCount>{t('reviews.helpfulCount', { count: review.helpful })}</HelpfulCount>
            )}
          </HelpfulSection>
        </ContentText>

        {/* Only show image section if review has an image */}
        {review.customerImage && (
          <CustomerImage>
            <ReviewImage src={review.customerImage} alt={`Review by ${customerName}`} />
            <ProductVariant>{productVariant}</ProductVariant>
          </CustomerImage>
        )}
      </ReviewContent>
    </Card>
  );
}

